import { useState, useRef, useCallback, useEffect } from 'react';
import { ImageCropper } from './ImageCropper';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface UploadedImage {
  id: string;
  previewUrl: string;
  file?: File;
  blob?: Blob;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  serverId?: string; // ID from server after upload
}

interface ImageUploaderProps {
  entryType: string;
  entryId?: string; // Optional - if not provided, operates in deferred mode
  existingImages?: { id: string; url: string }[];
  onImagesChange?: (images: { id: string; serverId?: string }[]) => void;
  onPendingBlobsChange?: (blobs: Blob[]) => void; // Called when pending blobs change (for deferred mode)
  disabled?: boolean;
}

export function ImageUploader({
  entryType,
  entryId,
  existingImages = [],
  onImagesChange,
  onPendingBlobsChange,
  disabled = false,
}: ImageUploaderProps) {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [editingImage, setEditingImage] = useState<{ index: number; src: string } | null>(null);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Sync with existingImages when they change (e.g., after async fetch)
  useEffect(() => {
    if (existingImages.length > 0) {
      setImages((prev) => {
        // Only add existing images that aren't already in state and haven't been deleted
        const existingIds = new Set(prev.filter(img => img.serverId).map(img => img.serverId));
        const newFromExisting = existingImages
          .filter(img => !existingIds.has(img.id) && !deletedIds.has(img.id))
          .map((img) => ({
            id: img.id,
            previewUrl: img.url,
            status: 'uploaded' as const,
            progress: 100,
            serverId: img.id,
          }));

        if (newFromExisting.length > 0) {
          return [...prev, ...newFromExisting];
        }
        return prev;
      });
    }
  }, [existingImages, deletedIds]);

  // Notify parent of pending blobs for deferred upload mode
  useEffect(() => {
    if (onPendingBlobsChange) {
      const pendingBlobs = images
        .filter((img) => img.status === 'pending' && (img.blob || img.file))
        .map((img) => (img.blob || img.file) as Blob);
      onPendingBlobsChange(pendingBlobs);
    }
  }, [images, onPendingBlobsChange]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Clear any previous permission error on successful file selection
    if (files.length > 0) {
      setPermissionError(null);
    }

    if (files.length === 0) return;

    // Reset input for re-selection
    e.target.value = '';

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) continue;

      const id = crypto.randomUUID();
      const previewUrl = URL.createObjectURL(file);

      // Add to state as pending (will show crop modal)
      setImages((prev) => [
        ...prev,
        { id, previewUrl, file, status: 'pending', progress: 0 },
      ]);

      // Open cropper for first new image
      setEditingImage({ index: images.length, src: previewUrl });
    }
  }, [images.length]);

  const handleCropSave = async (croppedImage: { blob: Blob; width: number; height: number }) => {
    if (!editingImage) return;

    const index = editingImage.index;
    const { blob: croppedBlob, width, height } = croppedImage;
    const previewUrl = URL.createObjectURL(croppedBlob);

    // Clean up old preview URL
    URL.revokeObjectURL(editingImage.src);
    setEditingImage(null);

    // If entryId is available (edit mode), upload immediately
    if (entryId) {
      // Set status to uploading
      setImages((prev) =>
        prev.map((img, i) =>
          i === index
            ? { ...img, blob: croppedBlob, previewUrl, status: 'uploading' }
            : img
        )
      );

      try {
        // Upload using FormData - dimensions already provided from crop
        const ext = await getPreferredExtension();
        const formData = new FormData();
        formData.append('file', croppedBlob, `image-${Date.now()}.${ext}`);
        formData.append('entryType', entryType);
        formData.append('entryId', entryId);
        formData.append('width', width.toString());
        formData.append('height', height.toString());

        const res = await fetch(apiUrl('/api/images'), {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!res.ok) {
          throw new Error('Upload failed');
        }

        const data = await res.json();

        setImages((prev) =>
          prev.map((img, i) =>
            i === index
              ? { ...img, status: 'uploaded', progress: 100, serverId: data.image.id }
              : img
          )
        );

        onImagesChange?.([]);
      } catch (err) {
        console.error('Upload error:', err);
        setImages((prev) =>
          prev.map((img, i) =>
            i === index
              ? { ...img, blob: croppedBlob, previewUrl, status: 'error' }
              : img
          )
        );
      }
    } else {
      // Deferred mode - just save the blob for later
      setImages((prev) =>
        prev.map((img, i) =>
          i === index
            ? { ...img, blob: croppedBlob, previewUrl, status: 'pending' }
            : img
        )
      );
    }
  };

  const handleCropCancel = () => {
    if (!editingImage) return;

    // Remove the pending image
    setImages((prev) => prev.filter((_, i) => i !== editingImage.index));
    URL.revokeObjectURL(editingImage.src);
    setEditingImage(null);
  };

  const uploadImage = async (index: number) => {
    if (!entryId) return; // Can't upload without entryId

    const image = images[index];
    if (!image || image.status === 'uploaded') return;

    const blobToUpload = image.blob || image.file;
    if (!blobToUpload) return;

    setImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, status: 'uploading', progress: 0 } : img))
    );

    try {
      // Convert to optimal format (AVIF or WebP)
      const ext = await getPreferredExtension();
      let finalBlob = blobToUpload;
      let width = 0;
      let height = 0;

      if (blobToUpload instanceof File && !blobToUpload.type.match(/^image\/(avif|webp)$/)) {
        const result = await convertToAVIF(blobToUpload);
        finalBlob = result.blob;
        width = result.width;
        height = result.height;
      } else if (blobToUpload instanceof Blob) {
        // Get dimensions from blob
        const img = new Image();
        const url = URL.createObjectURL(blobToUpload);
        await new Promise<void>((resolve) => {
          img.onload = () => {
            width = img.naturalWidth;
            height = img.naturalHeight;
            URL.revokeObjectURL(url);
            resolve();
          };
          img.src = url;
        });
      }

      // Upload using FormData
      const formData = new FormData();
      formData.append('file', finalBlob, `image-${Date.now()}.${ext}`);
      formData.append('entryType', entryType);
      formData.append('entryId', entryId);
      formData.append('width', width.toString());
      formData.append('height', height.toString());

      const res = await fetch(apiUrl('/api/images'), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        throw new Error('Upload failed');
      }

      const data = await res.json();

      setImages((prev) =>
        prev.map((img, i) =>
          i === index
            ? { ...img, status: 'uploaded', progress: 100, serverId: data.image.id }
            : img
        )
      );

      // Notify parent
      onImagesChange?.(
        images.map((img, i) =>
          i === index ? { id: img.id, serverId: data.image.id } : { id: img.id, serverId: img.serverId }
        )
      );
    } catch (err) {
      console.error('Upload error:', err);
      setImages((prev) =>
        prev.map((img, i) => (i === index ? { ...img, status: 'error', progress: 0 } : img))
      );
    }
  };

  const removeImage = async (index: number) => {
    const image = images[index];
    if (!image) return;

    // Track deleted server IDs to prevent re-adding from existingImages
    if (image.serverId) {
      setDeletedIds((prev) => new Set([...prev, image.serverId!]));
    }

    // Remove from UI immediately
    setImages((prev) => prev.filter((_, i) => i !== index));

    // If uploaded, delete from server
    if (image.serverId) {
      try {
        await fetch(apiUrl(`/api/images/${image.serverId}?confirm=true`), {
          method: 'DELETE',
          credentials: 'include',
        });
      } catch (err) {
        console.error('Delete error:', err);
      }
    }

    // Clean up preview URL
    if (image.previewUrl && !image.serverId) {
      URL.revokeObjectURL(image.previewUrl);
    }

    onImagesChange?.(images.filter((_, i) => i !== index).map((img) => ({ id: img.id, serverId: img.serverId })));
  };

  const openCropper = (index: number) => {
    const image = images[index];
    if (image) {
      setEditingImage({ index, src: image.previewUrl });
    }
  };

  // Upload all pending images
  const uploadAllPending = async () => {
    const pendingIndices = images
      .map((img, i) => (img.status === 'pending' ? i : -1))
      .filter((i) => i !== -1);

    for (const index of pendingIndices) {
      await uploadImage(index);
    }
  };

  const hasPending = images.some((img) => img.status === 'pending');

  const handleAddClick = useCallback(() => {
    setPermissionError(null);
    setShowPicker(true);
  }, []);

  return (
    <div className="space-y-4">
      {/* Image Grid */}
      <div className="grid grid-cols-3 gap-2">
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
          >
            <img
              src={image.previewUrl}
              alt={`Upload ${index + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Status Overlay */}
            {image.status === 'uploading' && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {image.status === 'error' && (
              <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center">
                <span className="text-white text-sm">Gagal</span>
              </div>
            )}

            {/* Actions */}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
              {image.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => openCropper(index)}
                  className="p-1 bg-white/90 rounded-full hover:bg-white"
                  title="Ubah"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="p-1 bg-white/90 rounded-full hover:bg-white text-red-600"
                title="Hapus"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Upload indicator */}
            {image.status === 'uploaded' && (
              <div className="absolute bottom-1 right-1 p-1 bg-green-500 rounded-full">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
        ))}

        {/* Add Button */}
        {!disabled && (
          <button
            type="button"
            onClick={handleAddClick}
            className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50 transition min-h-11"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-xs text-gray-500">Tambah</span>
          </button>
        )}
      </div>

      {/* Permission Error Message */}
      {permissionError && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm">{permissionError}</p>
          <p className="text-yellow-600 text-xs mt-1">
            Kamu perlu mengizinkan akses kamera di pengaturan browser atau perangkat.
          </p>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Source Picker Bottom Sheet */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowPicker(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl p-4 pb-[env(safe-area-inset-bottom,16px)] space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { setShowPicker(false); cameraInputRef.current?.click(); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Kamera</span>
            </button>
            <button
              type="button"
              onClick={() => { setShowPicker(false); fileInputRef.current?.click(); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Galeri</span>
            </button>
            <button
              type="button"
              onClick={() => setShowPicker(false)}
              className="w-full p-3 rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-50 active:bg-gray-100 transition"
            >
              Batal
            </button>
          </div>
        </div>
      )}

      {/* Upload All Button - only show when entryId is provided (non-deferred mode) */}
      {hasPending && !disabled && entryId && (
        <button
          type="button"
          onClick={uploadAllPending}
          className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
        >
          Upload Semua Gambar
        </button>
      )}

      {/* Cropper Modal */}
      {editingImage && (
        <ImageCropper
          imageSrc={editingImage.src}
          isOpen={true}
          onClose={handleCropCancel}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
}
